import { Estate, EstateJSON } from "../models/Estate";
import { callDelete, callGet, callPost, callPostWithFormData, callPut, laravelApiV1 } from "./index";
import { EstateSummary } from "../models/EstateSummary";
import { Asset, AssetJSON } from "../models/Asset";
import { ProbateType } from "models/ProbateType";
import { User } from "models/User";
import { UserProfile } from "models/UserProfile";

export type AssetImageUploadResult = {
  asset_id?: number;
  file_name?: string;
  url?: string;
};

export class AtticusEstate {
  async fetchEstates(userId:number, includeDeleted = false): Promise<Estate[] | undefined> {
    try {
      let path = `/users/${userId}/estates`;
      if (includeDeleted) path += "?deleted=true";
      const response = await callGet(path);
      if(!response?.data) {
        return undefined
      }

      return [Estate.fromJSON(response.data)]
    } catch (error) {
      console.error(error, "fetchEstates error");
    }
  }

  async fetchUsersInEstate(estateId: number): Promise<User[] | undefined> {
    try {
      const path = `/estates/${estateId}/users`
      const response = await callGet(path);
      return response?.data?.map((json: any) => {
        const u = User.fromJSON(json)
        u.profile = UserProfile.fromJSON(json.profile) || undefined
        return u
      });
    } catch (error) {
      console.error(error, "fetchUsersInEstate error");
    }
  }

  async addUserToEstate(estateId: number, userId: number): Promise<boolean> {
    try {
      await callPost(`/estates/${estateId}/users/${userId}`);
      return true;
    } catch (error) {
      console.error(
        (error as any)?.response?.statusText || "addUserToEstate error"
      );
      throw error;
    }
  }

  async fetchSingleEstate(estateId: number): Promise<Estate | undefined> {
    try {
      const response = await callGet( `/estates/${estateId}`, true);
      const estate = Estate.fromJSON(response?.data)
      if (response?.data?.probate_type) {
        estate.probateType = ProbateType.fromJSON(response.data.probate_type)
      }
      return estate
    } catch (error) {
      console.error(error, "fetchSingleEstate error");
    }
    return undefined;
  }

  async createEstate(userId:number, initialFields: EstateJSON): Promise<number> {
    try {
      const response = await callPost(`/users/${userId}/estates`, initialFields);
      return response?.data?.id;
    } catch (error) {
      console.error(error, "createEstate error");
      throw error;
    }
  }

  async updateEstate(id: number, updateFields: EstateJSON): Promise<boolean> {
    try {
      const fields = {
        ...updateFields,
      };
      
      if (updateFields.has_will === "1" || updateFields.has_will === 1 as any) {
        fields.has_will = "Yes";
      } else if (updateFields.has_will === "0" || updateFields.has_will === 0 as any) {
        fields.has_will = "No";
      }
      
      await callPut("/estates/" + id, fields);
      return true;
    } catch (error) {
      console.error(
        (error as any)?.response?.statusText || "updateEstate error"
      );
      throw error;
    }
  }

  async deleteEstate(id: number): Promise<boolean> {
    try {
      await callDelete("/estates/" + id);
      return true;
    } catch (error) {
      console.error(error, "deleteEstate error");
      throw error;
    }
  }

  async getSummary(
    estateId: number | string,
    userId: number,
  ): Promise<EstateSummary | undefined> {
    try {
      const response = await callGet(
        `/estates/${estateId}/users/${userId}/summary`
      );

      if (response?.data) {
        return EstateSummary.fromJSON(response.data);
      }
    } catch (error) {
      console.error(error, "getSummary error");
    }
    return undefined;
  }

  async getAssets(
    estateId: number,
    userId:number,
    excludeFI = false
  ): Promise<Asset[] | undefined> {
    try {
      const response = await callGet(`/estates/${estateId}/users/${userId}/assets${excludeFI ? "?nofi=true" : ""}`);
      return response?.data?.data?.assets?.map((json: AssetJSON) => Asset.fromJSON(json));
    } catch (error) {
      console.error(error, "getAssets error");
    }
    return undefined;
  }

  async saveAsset(
    asset?: Asset,
    estateId?: number,
    userId?: number
  ): Promise<number | undefined> {
    try {
      const url = `/estates/${estateId}/users/${userId}/assets${asset?.id ? "/" + asset?.id : ""}`;
      let response;
      if (asset?.id) {
        response = await callPut(url, asset?.toJson());
      } else {
        response = await callPost(url, asset?.toJson());
      }
      return response?.data?.data?.id;
    } catch (error) {
      console.error(error, "saveAsset error");
    }
    return undefined;
  }

  async deleteAsset(
    assetId: number,
    estateId: number
  ): Promise<number | undefined> {
    try {
      const url = `/estates/${estateId}/assets/${assetId}`;
      const response = await callDelete(url);
      return response?.data?.success || response?.data?.error;
    } catch (error) {
      console.error(error, "deleteAsset error");
    }
    return undefined;
  }

  async saveAssetImage(
    file?: File,
    estateId?: number,
    userId?: number,
    assetId?: number,
  ): Promise<AssetImageUploadResult | null> {
    if (file && assetId) {
      try {
        console.log("saving asset image", file, assetId, estateId, userId)
        const formData = new FormData();
        formData.append("image", file, file.name);
        formData.append("asset_id", assetId.toString());
        const response = await callPostWithFormData(
          `estates/${estateId}/users/${userId}/assets/${assetId}/image`,
          formData
        );
        return response?.data as AssetImageUploadResult;
      } catch (error) {
        console.error(error, "saveAssetImage error");
        return null;
      }
    }
    return null;
  }

  async getProbateType(estateId: number): Promise<ProbateType | undefined> {
    try {
      const response = await callGet(
        `/estates/${estateId}/probate-type`
      );
      if (response?.data) {
        const probateType = ProbateType.fromJSON(response?.data);
        return probateType;
      } else {
        throw Error("Failed to get probate type");
      }
    } catch (error) {
      console.error(error, "getProbateType error");
    }
    return undefined;
  }
}
